import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.js';
import { useSnap } from '../hooks/useSnap.js';
import { getEntitlements } from '../services/api.js';
import type { Entitlements } from '@snap-platform/shared';
import guardianLogo from '../assets/guardian-logo.svg';
import {
  ShieldCheckIcon,
  WalletIcon,
  PlugIcon,
  LockIcon,
} from './Icons.js';

function truncateAddress(address: string): string {
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

export function Navbar() {
  const {
    isAuthenticated,
    walletAddress,
    connectWallet,
    isConnecting,
    disconnect,
    token,
  } = useAuth();
  const snap = useSnap(token);
  const navigate = useNavigate();
  const location = useLocation();
  const isConnectPage = location.pathname === '/connect';
  const isHomepage = location.pathname === '/';
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [entitlements, setEntitlements] = useState<Entitlements | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Load entitlements when authenticated
  useEffect(() => {
    if (!token) {
      setEntitlements(null);
      return;
    }
    getEntitlements(token).then(setEntitlements).catch(() => {});
  }, [token]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClick);
      return () => document.removeEventListener('mousedown', handleClick);
    }
  }, [dropdownOpen]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
    setDropdownOpen(false);
  }, [location.pathname]);

  // Derived state
  const isPro = entitlements?.tier === 'pro';
  const snapOk = snap.isInstalled;

  // Protection status: green = Pro+Snap, amber = partial, red = disconnected
  const protectionStatus: 'green' | 'amber' | 'red' = !isAuthenticated
    ? 'red'
    : isPro && snapOk
      ? 'green'
      : 'amber';

  async function handleConnect() {
    try {
      await connectWallet();
    } catch {
      navigate('/connect');
    }
  }

  async function handleDisconnect() {
    setDropdownOpen(false);
    await disconnect();
  }

  return (
    <nav className="navbar">
      <div className="navbar-inner">

        {/* ── LEFT: Logo ── */}
        <Link to="/" className="navbar-brand">
          <img src={guardianLogo} alt="" className="navbar-logo" aria-hidden="true" />
          <span>CryptoGuardians</span>
        </Link>

        {/* ── CENTER: Links ── */}
        <ul className="navbar-links">
          <li><Link to="/wallet">Account Center</Link></li>
          <li><Link to="/account/security">Security</Link></li>
          <li><Link to="/pricing">Pricing</Link></li>
          <li><Link to="/support">Support</Link></li>
        </ul>

        {/* ── RIGHT: Wallet area ── */}
        <div className="navbar-actions">
          {isAuthenticated ? (
            <>
              {/* Upgrade CTA (free tier only) */}
              {!isPro && entitlements && (
                <Link to="/pricing" className="btn btn-primary btn-sm nav-upgrade-btn">
                  Upgrade
                </Link>
              )}

              {/* Wallet pill with dropdown */}
              <div className="nav-wallet-wrap" ref={dropdownRef}>
                <button
                  className="nav-wallet-pill"
                  onClick={() => setDropdownOpen((v) => !v)}
                  aria-expanded={dropdownOpen}
                  aria-haspopup="true"
                >
                  <span className={`nav-status-dot nav-status-dot-${protectionStatus}`} />
                  {walletAddress && (
                    <span className="nav-wallet-addr">{truncateAddress(walletAddress)}</span>
                  )}
                </button>

                {/* Dropdown */}
                {dropdownOpen && (
                  <div className="nav-dropdown">
                    {/* Protection status */}
                    <div className="nav-dropdown-section">
                      <div className="nav-dropdown-label">Protection Status</div>
                      <div className="nav-dropdown-status">
                        <div className="nav-dropdown-row">
                          <span>Plan</span>
                          <span className="nav-dropdown-val">
                            <span className={`nav-status-dot nav-status-dot-${isPro ? 'green' : 'amber'}`} />
                            {isPro ? 'Pro' : 'Free'}
                          </span>
                        </div>
                        <div className="nav-dropdown-row">
                          <span>Snap</span>
                          <span className="nav-dropdown-val">
                            <span className={`nav-status-dot nav-status-dot-${snapOk ? 'green' : 'amber'}`} />
                            {snapOk ? 'Installed' : 'Not Installed'}
                          </span>
                        </div>
                        {entitlements && (
                          <div className="nav-dropdown-row">
                            <span>Scans</span>
                            <span className="nav-dropdown-val">
                              {entitlements.scansRemaining} / {entitlements.monthlyLimit}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="nav-dropdown-divider" />

                    {/* Navigation */}
                    <div className="nav-dropdown-section">
                      <Link to="/wallet" className="nav-dropdown-link" onClick={() => setDropdownOpen(false)}>
                        <ShieldCheckIcon className="nav-dropdown-link-icon" aria-hidden="true" />
                        Dashboard
                      </Link>
                      <Link to="/wallet" className="nav-dropdown-link" onClick={() => setDropdownOpen(false)}>
                        <PlugIcon className="nav-dropdown-link-icon" aria-hidden="true" />
                        Install Snap
                      </Link>
                      <Link to="/recover" className="nav-dropdown-link" onClick={() => setDropdownOpen(false)}>
                        <LockIcon className="nav-dropdown-link-icon" aria-hidden="true" />
                        Restore Subscription
                      </Link>
                      <Link to="/transfer" className="nav-dropdown-link" onClick={() => setDropdownOpen(false)}>
                        <WalletIcon className="nav-dropdown-link-icon" aria-hidden="true" />
                        Transfer Subscription
                      </Link>
                    </div>

                    <div className="nav-dropdown-divider" />

                    {/* Disconnect */}
                    <button className="nav-dropdown-disconnect" onClick={handleDisconnect}>
                      Disconnect Wallet
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : !isConnectPage && !isHomepage ? (
            <button
              className="btn btn-primary btn-sm"
              onClick={handleConnect}
              disabled={isConnecting}
            >
              {isConnecting ? 'Connecting...' : 'Connect Wallet'}
            </button>
          ) : null}
        </div>

        {/* ── Hamburger ── */}
        <button
          className="hamburger"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? '\u2715' : '\u2630'}
        </button>
      </div>

      {/* ── Mobile Menu ── */}
      {mobileOpen && (
        <div className="mobile-menu" onClick={() => setMobileOpen(false)}>
          <Link to="/wallet">Account Center</Link>
          <Link to="/account/security">Security</Link>
          <Link to="/pricing">Pricing</Link>
          <Link to="/support">Support</Link>

          <div className="nav-mobile-divider" />

          {isAuthenticated ? (
            <>
              {/* Status summary */}
              <div className="nav-mobile-status">
                <span className={`nav-status-dot nav-status-dot-${protectionStatus}`} />
                <span>
                  {walletAddress ? truncateAddress(walletAddress) : 'Connected'}
                  {' \u2022 '}
                  {isPro ? 'Pro' : 'Free'}
                </span>
              </div>

              {!isPro && entitlements && (
                <a className="btn btn-primary btn-sm nav-mobile-upgrade" href="/pricing">
                  Upgrade
                </a>
              )}

              <Link to="/recover">Restore Subscription</Link>
              <Link to="/transfer">Transfer Subscription</Link>

              <div className="nav-mobile-divider" />

              <button className="nav-mobile-disconnect" onClick={() => { setMobileOpen(false); disconnect(); }}>
                Disconnect Wallet
              </button>
            </>
          ) : !isConnectPage && !isHomepage ? (
            <button
              className="btn btn-primary btn-sm"
              onClick={() => { setMobileOpen(false); handleConnect(); }}
              disabled={isConnecting}
            >
              {isConnecting ? 'Connecting...' : 'Connect Wallet'}
            </button>
          ) : null}
        </div>
      )}
    </nav>
  );
}
